import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  Classes,
  Button,
  Intent,
  Callout,
  Spinner,
  Tag,
} from '@blueprintjs/core'
import { useSegmentationPriorities } from '../../contexts/SegmentationPrioritiesProvider'
import { DeletionConsequences, DependencyCleanupInfo } from './DeletionConsequences'

/**
 * Unified deletion dialog for single and batch segmentation deletion.
 *
 * Pass a single-element array for individual deletion or a multi-element
 * array for batch deletion — the dialog adapts its title, callout, and
 * API calls accordingly.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Array} props.segmentations - Array of segmentation objects to delete.
 * @param {Function} props.onDeleted - Called after successful deletion.
 */
export function SegmentationDeletionDialog({
  isOpen,
  onClose,
  segmentations = [],
  onDeleted,
}) {
  const isBatch = segmentations.length > 1

  const [analysis, setAnalysis] = useState(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    analyzeSegmentationDeletion,
    deleteSegmentation,
    analyzeBatchSegmentationDeletion,
    deleteSegmentations,
  } = useSegmentationPriorities()

  const abortControllerRef = useRef(null)
  const requestIdRef = useRef(0)
  const isSubmittingRef = useRef(false)

  useEffect(() => {
    if (isOpen && segmentations.length > 0 && !isDeleting) {
      analyzeConsequences()
    }
    if (!isOpen) {
      setAnalysis(null)
      setIsDeleting(false)
      isSubmittingRef.current = false
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [isOpen, segmentations])

  const analyzeConsequences = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    const currentRequestId = ++requestIdRef.current

    setIsAnalyzing(true)
    setAnalysis(null)

    const result = isBatch
      ? await analyzeBatchSegmentationDeletion(
          segmentations.map(s => s.id),
          { signal: controller.signal }
        )
      : await analyzeSegmentationDeletion(segmentations[0].id, {
          signal: controller.signal,
        })

    if (currentRequestId !== requestIdRef.current) return
    if (controller.signal.aborted) return
    if (result) {
      setAnalysis(result)
    } else {
      onClose()
    }
    setIsAnalyzing(false)
  }

  const handleDelete = async () => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsDeleting(true)
    try {
      const result = isBatch
        ? await deleteSegmentations(segmentations.map(s => s.id))
        : await deleteSegmentation(segmentations[0].id)
      if (result) {
        onDeleted()
        onClose()
      }
    } finally {
      isSubmittingRef.current = false
      setIsDeleting(false)
    }
  }

  const title = isBatch
    ? `Delete ${segmentations.length} Segmentations`
    : 'Delete Segmentation'

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      style={{ width: 500 }}
    >
      <div className={Classes.DIALOG_BODY}>
        {isAnalyzing && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spinner size={30} />
            <p style={{ marginTop: 10 }}>Analyzing deletion impact...</p>
          </div>
        )}

        {!isAnalyzing && analysis && (
          <>
            {isBatch ? (
              <Callout icon="info-sign" style={{ marginBottom: 15 }}>
                <strong>
                  {analysis.impact_summary.count} segmentation(s)
                </strong>{' '}
                will be deleted along with their variables and questions:
                <div
                  style={{
                    marginTop: 5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 4,
                  }}
                >
                  {analysis.impact_summary.segmentations.map(seg => (
                    <Tag key={seg.id} minimal>
                      {seg.name}
                    </Tag>
                  ))}
                </div>
              </Callout>
            ) : (
              <Callout icon="info-sign" style={{ marginBottom: 15 }}>
                Segmentation{' '}
                <strong>{analysis.impact_summary.segmentation.name}</strong>{' '}
                will be deleted along with its variable and question.
              </Callout>
            )}

            <DependencyCleanupInfo
              dependencyCleanup={analysis.dependency_cleanup}
            />

            <DeletionConsequences
              warnings={analysis.warnings}
              consequences={analysis.consequences}
            />
          </>
        )}
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            intent={Intent.DANGER}
            onClick={handleDelete}
            disabled={!analysis || isDeleting || isAnalyzing}
            loading={isDeleting}
          >
            Confirm Deletion
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
